import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Field } from '../ui/Field';
import { NumberStepper } from '../ui/NumberStepper';
import { Sheet } from '../ui/Sheet';
import { RCU_LIMITS } from '../../../lib/rcu/catalog/limits';
import { isValidSerial, serialHost } from '../../../lib/rcu/transport';
import type { RcuIdentity } from '../../../lib/rcu/model';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useToastStore } from '../../../stores/toastStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface IdentitySheetProps {
  isOpen: boolean;
  identity: RcuIdentity;
  onClose: () => void;
}

/** Name, device id, network address and onboard output count. */
export const IdentitySheet: React.FC<IdentitySheetProps> = ({ isOpen, identity, onClose }) => {
  const updateIdentity = useRcuConfigStore((state) => state.updateIdentity);
  const setOutputCount = useRcuConfigStore((state) => state.setOutputCount);
  const report = useCommandFeedback();
  const error = useToastStore((state) => state.error);

  const [draft, setDraft] = React.useState(identity);
  const [outputCount, setCount] = React.useState(identity.outputCount);

  const patch = (next: Partial<RcuIdentity>): void => setDraft((current) => ({ ...current, ...next }));

  const serialProblem =
    draft.serialNumber.trim() !== '' && !isValidSerial(draft.serialNumber)
      ? 'Letters, digits and dashes only.'
      : null;

  const handleSave = (): void => {
    if (serialProblem !== null) {
      error(serialProblem);
      return;
    }

    const identitySaved = report(updateIdentity(draft), 'RCU details saved.');
    const countSaved =
      outputCount === identity.outputCount
        ? true
        : report(setOutputCount(outputCount), `Onboard outputs set to ${outputCount}.`);

    if (identitySaved && countSaved) onClose();
  };

  return (
    <Sheet
      isOpen={isOpen}
      title="RCU details"
      subtitle="Identify this controller and its onboard outputs."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field
          label="RCU name"
          value={draft.name}
          onChange={(name) => patch({ name })}
          placeholder="Room 101 controller"
        />
        <Field
          label="Device ID"
          value={draft.deviceId}
          onChange={(deviceId) => patch({ deviceId })}
          placeholder="TYM-RCU-0001"
          hint="Printed on the controller. Used to recover a forgotten PIN."
        />
        <Field
          label="Serial number"
          value={draft.serialNumber}
          onChange={(serialNumber) => patch({ serialNumber })}
          placeholder="888854"
          hint={
            draft.serialNumber.trim() === ''
              ? 'The number the controller reports. Used to find it on the network.'
              : `Found on the network as ${serialHost(draft.serialNumber)}`
          }
        />
        <Field
          label="IP address"
          value={draft.ipAddress}
          onChange={(ipAddress) => patch({ ipAddress })}
          placeholder="192.168.0.108"
          inputMode="decimal"
          hint="Optional. Set it to skip name resolution, which some tablets cannot do."
        />
        <Field
          label="Location"
          value={draft.location}
          onChange={(location) => patch({ location })}
          placeholder="Level 3 riser"
        />
        <NumberStepper
          label="Onboard outputs"
          value={outputCount}
          min={RCU_LIMITS.MIN_OUTPUTS}
          max={RCU_LIMITS.MAX_OUTPUTS}
          onChange={setCount}
          hint="Relay outputs built into the RCU. Loads wired above this count block a reduction."
        />
      </div>
    </Sheet>
  );
};

export default IdentitySheet;
